/**
 * Button Component
 * 
 * Unified button component replacing btn-primary, btn-secondary, and inline button styles.
 * 
 * Features:
 * - Multiple variants (primary, secondary, ghost, danger)
 * - Size options (small, medium, large)
 * - Loading state with spinner
 * - Icon support (left/right)
 * - Full TypeScript support
 * - Accessibility built-in
 * 
 * Usage:
 *   <Button>Click me</Button>
 *   <Button variant="secondary" onClick={handleClick}>Cancel</Button>
 *   <Button variant="primary" loading disabled>Saving...</Button>
 *   <Button size="small" icon={<Icon />}>With Icon</Button>
 * 
 * CHANGELOG:
 * - 2025-10-13: Initial creation - replaces btn-primary/btn-secondary classes
 */

import React, { ButtonHTMLAttributes, ReactNode } from 'react';

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
type ButtonSize = 'small' | 'medium' | 'large';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Button style variant */
  variant?: ButtonVariant;
  /** Button size */
  size?: ButtonSize;
  /** Show loading spinner and disable button */
  loading?: boolean;
  /** Icon to display before text */
  iconLeft?: ReactNode;
  /** Icon to display after text */
  iconRight?: ReactNode;
  /** Full width button */
  fullWidth?: boolean;
  /** Button content */
  children: ReactNode;
}

export default function Button({
  variant = 'primary',
  size = 'medium',
  loading = false,
  iconLeft,
  iconRight,
  fullWidth = false,
  disabled,
  children,
  style,
  className,
  ...props
}: ButtonProps) {
  // Variant styles
  const variantStyles: Record<ButtonVariant, React.CSSProperties> = {
    primary: {
      backgroundColor: 'var(--color-accent)',
      color: '#0b0b0b',
      border: 'none',
    },
    secondary: {
      backgroundColor: 'transparent',
      color: 'var(--color-accent)',
      border: '2px solid var(--color-accent)',
    },
    ghost: {
      backgroundColor: 'transparent',
      color: 'var(--color-text)',
      border: '1px solid var(--color-border)',
    },
    danger: {
      backgroundColor: '#ef4444',
      color: 'white',
      border: 'none',
    },
  };

  // Size styles
  const sizeStyles: Record<ButtonSize, React.CSSProperties> = {
    small: {
      padding: '0.5rem 1rem',
      fontSize: '0.875rem',
      borderRadius: '999px',
    },
    medium: {
      padding: '0.875rem 1.5rem',
      fontSize: '1rem',
      borderRadius: '999px',
    },
    large: {
      padding: '1rem 2rem',
      fontSize: '1.125rem',
      borderRadius: '999px',
    },
  };

  // Base styles
  const baseStyles: React.CSSProperties = {
    fontFamily: 'inherit',
    fontWeight: '600',
    cursor: disabled || loading ? 'not-allowed' : 'pointer',
    transition: 'all 0.2s ease',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    width: fullWidth ? '100%' : 'auto',
    opacity: disabled || loading ? 0.6 : 1,
    ...variantStyles[variant],
    ...sizeStyles[size],
    ...style,
  };

  console.log(`[BUTTON RENDER] variant=${variant}, size=${size}, loading=${loading}, disabled=${disabled || false}`);

  return (
    <button
      style={baseStyles}
      disabled={disabled || loading}
      className={className}
      {...props}
    >
      {loading && (
        <div 
          style={{ 
            border: '2px solid currentColor',
            borderTopColor: 'transparent',
            borderRadius: '50%',
            width: size === 'small' ? '14px' : '16px',
            height: size === 'small' ? '14px' : '16px',
            animation: 'spin 0.8s linear infinite',
          }}
        />
      )}
      {!loading && iconLeft && <span style={{ display: 'flex' }}>{iconLeft}</span>}
      {children}
      {!loading && iconRight && <span style={{ display: 'flex' }}>{iconRight}</span>}
    </button>
  );
}

/**
 * Usage Examples:
 * 
 * // Basic
 * <Button>Click me</Button>
 * 
 * // With variant
 * <Button variant="secondary">Cancel</Button>
 * 
 * // Loading state
 * <Button loading>Saving...</Button>
 * 
 * // Full width
 * <Button fullWidth>Submit Form</Button>
 * 
 * // With icons
 * <Button iconLeft={<Plus size={16} />}>Add Item</Button>
 * 
 * // Small danger button
 * <Button variant="danger" size="small">Delete</Button>
 * 
 * // Disabled
 * <Button disabled>Cannot Click</Button>
 */

